import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { BigTextPreview } from "../../components/big-text-preview";
import { Button } from "../../components/button";
import { CodeEditor } from "../../components/code-editor";
import { CommandBar } from "../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { DataPickerControl, MultiDataPickerControl } from "../../components/data-picker";
import { Editor, EditorDescription, EditorTitle } from "../../components/editor";
import { Checkbox, NumberInput, Select, TextArea, TextInput } from "../../components/form";
import { Icon } from "../../components/icon";
import { JsonControl } from "../../components/json-control";
import { Link } from "../../components/link";
import { Section } from "../../components/layout";
import { ModelPickerControl } from "../../components/model-picker";
import { SpecializedControl } from "../../components/specializedObject";
import { TabControl } from "../../components/tabcontrol";
import { CheckWrapper, MutedText, Paragraph, TextHeading } from "../../components/text";
import { Constants } from "../../constants";
import { displayFormControl } from "../../ui/alert";
import { runOperation } from "../../ui/operation";
import { find, valueFrom } from "../../util";
import { BuiltinTools, DefaultWorkflowOptions } from "./shared";
import { ModelBadge } from "../../components/model-icon";
import { requestAiEdit } from "../../ui/ai-edit";
import { acceptDiff } from "../../ui/accept-diff";

export default function (ctx) {
    const workflowId = ctx.params.workflowId;
    const isCreating = workflowId === "create";
    const workflow = el.state.create(isCreating ? {
        id: "create",
        title: DefaultWorkflowOptions.title,
        options: DefaultWorkflowOptions
    } : null);

    if (!isCreating) {
        runOperation("Loading workflow...", async () => {
            const response = await api.getJson(`/api/v1/batch/workflows/${workflowId}`);
            workflow.value = response.data;
        });
    }

    return el.state.listening([workflow], () => {
        const rawOptions = workflow.value?.options || workflow.value?.batchWorkflowOptions || DefaultWorkflowOptions;
        const options = {
            ...DefaultWorkflowOptions,
            ...rawOptions,
            enabledTools: {
                ...DefaultWorkflowOptions.enabledTools,
                ...(rawOptions.enabledTools || {}),
                options: {
                    ...DefaultWorkflowOptions.enabledTools.options,
                    ...(rawOptions.enabledTools?.options || {})
                }
            }
        };
        const title = isCreating ? "New batch workflow" : (workflow.value?.title || `Workflow ${workflow.value?.id ? "#" + workflow.value.id.slice(-6) : "-"}`);

        return Dashboard(
            Section(
                Link(el.fragment(Icon("arrow-left-line"), "Back to Batch"), null, {
                    onClick() {
                        history.go(-1);
                    }
                })
            ),
            DashboardTitle(title, "Configure how future batch job items will be processed."),
            Section(
                CommandBar(
                    Button(
                        Icon("save-line"),
                        isCreating ? "Create workflow" : "Save changes",
                        {
                            onClick() {
                                const enabledToolOptions = valueFrom("batchEnabledToolOptions") || {};
                                const validationEnabled = !!valueFrom("batchValidationEnabled");
                                const data = {
                                    title: valueFrom("batchWorkflowTitle")?.nullWhenEmpty(),
                                    instruction: valueFrom("batchInstruction"),
                                    resultSchema: valueFrom("batchResultSchema"),
                                    modelName: valueFrom("batchModelName"),
                                    reasoningEffort: valueFrom("batchReasoningEffort"),
                                    validationInstruction: validationEnabled ? valueFrom("batchValidationInstruction")?.nullWhenEmpty() : null,
                                    enabledTools: {
                                        enabledFunctions: valueFrom("batchEnabledTools") || [],
                                        options: {
                                            webSearchMode: enabledToolOptions.webSearchMode || "Summarized",
                                            webSearchMaxResults: Number(enabledToolOptions.webSearchMaxResults || 10),
                                            imageGenerationQuality: enabledToolOptions.imageGenerationQuality || "Medium",
                                            imageGenerationMaxResults: Number(enabledToolOptions.imageGenerationMaxResults || 2),
                                            imageGenerationAllowMatureContent: !!enabledToolOptions.imageGenerationAllowMatureContent
                                        }
                                    },
                                    errorStopThreshold: Number(valueFrom("batchErrorStopThreshold")),
                                    maxRetries: Number(valueFrom("batchMaxRetries"))
                                };

                                runOperation(isCreating ? "Creating workflow..." : "Saving workflow...", async () => {
                                    if (isCreating) {
                                        const response = await api.postJson("/api/v1/batch/workflows", data);
                                        page(`/dashboard/batch/workflows/${response.data.id}`);
                                    } else {
                                        const response = await api.patchJson(`/api/v1/batch/workflows/${workflowId}`, data);
                                        workflow.value = response.data;
                                    }
                                });
                            }
                        }),
                    Button(
                        Icon("quill-pen-ai-line"),
                        "Edit with AI",
                        {
                            onClick() {
                                const validationEnabled = !!valueFrom("batchValidationEnabled");
                                const inputData = {
                                    instructions: valueFrom("batchInstruction"),
                                    resultSchema: valueFrom("batchResultSchema"),
                                    validationInstruction: validationEnabled ? valueFrom("batchValidationInstruction")?.nullWhenEmpty() : null,
                                };
                                requestAiEdit({
                                    inputData: inputData,
                                    subject: "batch workflow",
                                    description: "Edit the instructions for this batch workflow.",
                                    responseSchema: {
                                        type: "object",
                                        properties: {
                                            instructions: { type: "string", description: "Detailed instructions for this batch workflow" },
                                            resultSchema: { type: "object", description: "JSON schema for expected job output" },
                                            validationInstruction: { type: "string", description: "Instructions for an judge model for validating job outputs." }
                                        }
                                    }
                                }).then((result) => {
                                    acceptDiff(JSON.stringify(inputData, null, 2), JSON.stringify(result, null, 2)).then(_ => {
                                        find("batchInstruction").value = result.instructions;
                                        find("batchResultSchema").value = result.resultSchema;
                                        if (validationEnabled) {
                                            find("batchValidationInstruction").value = result.validationInstruction;
                                        }
                                    })
                                })
                            }
                        }
                    )
                )
            ),
            Section(
                TabControl([
                    {
                        title: "Basic",
                        icon: Icon("file-settings-line"),
                        contents: el.fragment(
                            Section(
                                Editor(
                                    {
                                        id: "batchWorkflowTitle",
                                        value: workflow.value?.title || "",
                                        renderValue(value) {
                                            return Button(value || MutedText("Untitled workflow"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: TextInput(null, "Workflow title", { value }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Workflow title"),
                                        Paragraph("Friendly name used to identify this workflow in batch lists and job creation.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "batchInstruction",
                                        value: options.instruction,
                                        renderValue(value) {
                                            return BigTextPreview(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: CodeEditor({ value, language: "markdown", height: "420px" }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => String(x || "").trim().length > 0, "Workflow instruction is required."]
                                                ]
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Workflow instructions"),
                                        Paragraph("Processing instruction sent to the model for every item.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "batchResultSchema",
                                        value: options.resultSchema,
                                        renderValue(value) {
                                            return BigTextPreview(JSON.stringify(value, null, 2), "json");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: JsonControl({ value }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => x && typeof x === "object", "Result schema must be a JSON object."]
                                                ]
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Result schema"),
                                        Paragraph("JSON schema that defines the expected result object.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Model",
                        icon: Icon("ai-generate-2-line"),
                        contents: el.fragment(
                            Section(
                                Editor(
                                    {
                                        id: "batchModelName",
                                        value: options.modelName,
                                        renderValue(value) {
                                            return value && ModelBadge(value) || MutedText("No model selected");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: "Choose model",
                                                message: "Choose the routed model or AI Gateway used by this batch workflow.",
                                                dialogSize: "large",
                                                formControlItem: ModelPickerControl({ selectedModel: value, listGateways: true }),
                                                onConfirm: callback,
                                                validate: [
                                                    [x => !!x, "Select a model before saving."]
                                                ]
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Model"),
                                        Paragraph("Model used to process each queued item and to run validation when validation is enabled.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "batchReasoningEffort",
                                        value: options.reasoningEffort,
                                        renderValue(value) {
                                            return Button(value || MutedText("Auto"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: DataPickerControl({
                                                    value,
                                                    allowNone: false,
                                                    data: Constants.NullableReasoningEffortSelectItems
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Reasoning effort"),
                                        Paragraph("Optional reasoning budget sent to models that support reasoning controls.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "batchEnabledTools",
                                        value: options.enabledTools?.enabledFunctions || [],
                                        renderValue(value) {
                                            return Button(value?.length ? `${value.length} tool(s)` : MutedText("No tools"));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "medium",
                                                formControlItem: MultiDataPickerControl({
                                                    value,
                                                    data: BuiltinTools.map(tool => ({
                                                        value: tool.value,
                                                        text: TextHeading(Icon(tool.icon), tool.label)
                                                    }))
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Enabled tools"),
                                        Paragraph("Built-in tools the model may call while processing each item.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "batchEnabledToolOptions",
                                        value: options.enabledTools?.options || {},
                                        renderValue() {
                                            return Button("Edit options");
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: SpecializedControl({
                                                    value,
                                                    fields: [
                                                        {
                                                            name: "webSearchMode",
                                                            label: "Web search mode",
                                                            control: Select({
                                                                value: value?.webSearchMode || "Summarized",
                                                                items: [
                                                                    { value: "Summarized" },
                                                                    { value: "Full" }
                                                                ]
                                                            })
                                                        },
                                                        {
                                                            name: "webSearchMaxResults",
                                                            label: "Web search max results",
                                                            control: TextInput(null, null, {
                                                                type: "number",
                                                                value: value?.webSearchMaxResults || 10,
                                                                min: 1,
                                                                max: 25,
                                                                step: 1
                                                            })
                                                        },
                                                        {
                                                            name: "imageGenerationQuality",
                                                            label: "Image quality",
                                                            control: Select({
                                                                value: value?.imageGenerationQuality || "Medium",
                                                                items: [
                                                                    { value: "Low" },
                                                                    { value: "Medium" },
                                                                    { value: "High" }
                                                                ]
                                                            })
                                                        },
                                                        {
                                                            name: "imageGenerationMaxResults",
                                                            label: "Image max results",
                                                            control: TextInput(null, null, {
                                                                type: "number",
                                                                value: value?.imageGenerationMaxResults || 2,
                                                                min: 1,
                                                                max: 8,
                                                                step: 1
                                                            })
                                                        },
                                                        {
                                                            name: "imageGenerationAllowMatureContent",
                                                            label: "Allow mature image content",
                                                            control: Checkbox(null, "Allow")
                                                        }
                                                    ]
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Tool options"),
                                        Paragraph("Limits and behavior for enabled tools such as web search and image generation.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Validation",
                        icon: Icon("verified-badge-line"),
                        contents: el.fragment(
                            Section(
                                Editor(
                                    {
                                        id: "batchValidationEnabled",
                                        value: !!options.validationInstruction,
                                        renderValue(value) {
                                            return CheckWrapper(value, "Enabled", "Disabled");
                                        },
                                        manipulateValue(value, callback) {
                                            callback(!!!value);
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Enable validation"),
                                        Paragraph("Runs a second model pass to check whether each output follows the workflow rules.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "batchValidationInstruction",
                                        value: options.validationInstruction || "",
                                        renderValue(value) {
                                            return BigTextPreview(value);
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                dialogSize: "large",
                                                formControlItem: TextArea(null, value || "", {
                                                    rows: 12
                                                }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Validation instructions"),
                                        Paragraph("Instruction used by a second model pass to approve or reject each result.")
                                    )
                                )
                            )
                        )
                    },
                    {
                        title: "Handling",
                        icon: Icon("settings-3-line"),
                        contents: el.fragment(
                            Section(
                                Editor(
                                    {
                                        id: "batchErrorStopThreshold",
                                        value: options.errorStopThreshold,
                                        renderValue(value) {
                                            return Button(String(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 1, max: 100, step: 1 }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Error stop threshold"),
                                        Paragraph("Consecutive item errors before the job is paused.")
                                    )
                                ),
                                Editor(
                                    {
                                        id: "batchMaxRetries",
                                        value: options.maxRetries,
                                        renderValue(value) {
                                            return Button(String(value));
                                        },
                                        manipulateValue(value, callback) {
                                            displayFormControl({
                                                title: this.editorTitle,
                                                message: this.editorParagraphs,
                                                formControlItem: NumberInput({ value, min: 0, max: 10, step: 1 }),
                                                onConfirm: callback,
                                                validate: []
                                            });
                                        }
                                    },
                                    EditorDescription(
                                        EditorTitle("Max retries per item"),
                                        Paragraph("Local retries for inference and validation failures.")
                                    )
                                )
                            )
                        )
                    }
                ], {
                    type: "fixed"
                })
            )
        );
    });
}
